import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { DropDown, Input } from "../../../Common";
import Button from "../../../Common/Button";
import styles from "./PartnerAndPlan.module.css";
import {
  getUsersPartnerList,
  getPartnerPlan,
  AddPartnerPlan,
} from "../../../api";
import { productType } from "../../../utils/utils";
import { DatePicker } from "../../../Common";

const PartnerAndPlan = ({ custId, featureAccess, userData }) => {
  const [partnerList, setPartnerList] = useState([]);
  const [planList, setPlanList] = useState([]);
  const [form, setForm] = useState({
    partner: "",
    plan: "",
    comment: "",
    subsRenewDate: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (userData?.partnerCode) {
      setForm((prev) => ({ ...prev, partner: userData.partnerCode }));
    }
  }, [userData]);

  useEffect(() => {
    fetchPartnerList();
  }, []);

  const fetchPartnerList = async () => {
    try {
      const resp = await getUsersPartnerList({
        appName: productType.INVESTING,
      });
      if (resp?.data?.code === 200) {
        const list = resp.data.data.map((item) => ({
          label: item.partnerName,
          value: item.partnerCd,
          partnerId: item.partnerId,
        }));
        setPartnerList(list);
      }
    } catch (error) {
      console.error("Partner fetch failed:", error);
    }
  };

  useEffect(() => {
    if (form.partner) fetchPlanList(form.partner);
    else setPlanList([]);
  }, [form.partner]);

  const fetchPlanList = async (partnerCode) => {
    try {
      const resp = await getPartnerPlan(partnerCode, {});
      if (resp?.data?.code === 200) {
        const list = resp.data.data.map((item) => ({
          label: item.planCode,
          value: item.planId,
          partnerId: item.partnerId,
          subscriptionFlg: item.subscriptionFlg,
        }));
        setPlanList(list);
      } else {
        setPlanList([]);
      }
    } catch (error) {
      console.error("Plan fetch failed:", error);
      setPlanList([]);
    }
  };

  const availablePlan = planList.find((p) => p.value === form.plan);
  const isSubscriptionPlan = availablePlan?.subscriptionFlg === true;
  const handleChange = (field, value) => {
    if (field === "partner") {
      setForm((prev) => ({
        ...prev,
        partner: value,
        plan: "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }

    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const now = new Date();

    if (!form.partner) newErrors.partner = "Partner is required";
    if (!form.comment.trim()) newErrors.comment = "Comment is required";

    if (form.subsRenewDate) {
      const end = new Date(form.subsRenewDate);
      if (end <= now) {
        newErrors.subsRenewDate = "Renewable time must be after current time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!validateForm()) return;

    const selectedPartner = partnerList.find((p) => p.value === form.partner);
    const selectedPlan = planList.find((p) => p.value === form.plan);
    const payload = {
      planId: selectedPlan?.value || "",
      partnerCode: selectedPartner?.value,
      comment: form.comment,
      custId,
      subsRenewDate: form.subsRenewDate
        ? new Date(form.subsRenewDate)
            .toISOString()
            .replace("T", " ")
            .split(".")[0] + ":999"
        : "",
    };

    try {
      setLoading(true);
      const resp = await AddPartnerPlan(payload);
      if (resp?.data?.code === 200) {
        setMessage({
          type: "success",
          text: "Partner Plan added successfully!",
        });
        setForm({
          partner: userData?.partnerCode || "",
          plan: "",
          comment: "",
        });

        setTimeout(() => {
          setMessage({ type: "", text: "" });
          window.location.href = `?id=${custId}&tab=DE`;
        }, 3000);
      } else {
        setMessage({
          type: "error",
          text: resp?.data?.message || "Failed to add Partner Plan.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Something went wrong while submitting.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.createRoleCard}>
        <div className={styles.card}>
          <form onSubmit={handleSubmit}>
            <h3 style={{ color: "white" }}>Add Partner & Plan</h3>

            <Row className="my-3">
              <Col md={12}>
                <DropDown
                  label="Partner"
                  value={form.partner}
                  data={partnerList}
                  onChange={(val) => handleChange("partner", val)}
                  placeholder="Select Partner"
                  height="40px"
                  required
                  error={!!errors.partner}
                  helperText={errors.partner}
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col md={12}>
                <DropDown
                  label="Plan"
                  value={form.plan}
                  data={planList}
                  onChange={(val) => handleChange("plan", val)}
                  placeholder="Select Plan"
                  height="40px"
                  error={!!errors.plan}
                  helperText={errors.plan}
                />
              </Col>
            </Row>
            {isSubscriptionPlan && (
              <div className={styles.fieldstyle}>
                <Row className="my-3">
                  <label className={styles.label}>
                    Subscription Renew Date
                  </label>

                  <DatePicker
                    selected={form.subsRenewDate}
                    onChange={(date) => handleChange("subsRenewDate", date)}
                    placeholderText="Select Subscription Renew Date"
                    showTime={true}
                    dateFormat="yyyy-MM-dd HH:mm:ss"
                    minDate={new Date()}
                    className={styles.datePicker}
                  />

                  {errors.subsRenewDate && (
                    <div className={styles.errorMessage}>
                      {errors.subsRenewDate}
                    </div>
                  )}
                </Row>
              </div>
            )}
            <Row className="my-3">
              <Col md={12}>
                <Input
                  label="Comment"
                  placeholder="Enter comment"
                  name="comment"
                  value={form.comment}
                  onChange={(e) => handleChange("comment", e.target.value)}
                  isTextArea
                  required
                  inputClassName={styles.commentBox}
                  error={!!errors.comment}
                  helperText={errors.comment}
                />
              </Col>
            </Row>

            {message.text && (
              <p
                style={{
                  color: message.type === "success" ? "green" : "red",
                  textAlign: "center",
                }}
              >
                {message.text}
              </p>
            )}
            <Row className="my-5">
              <Col md={6}>
                {featureAccess?.writeAccess ? (
                  <Button
                    onClick={handleSubmit}
                    title="Submit"
                    loading={loading}
                    disabled={loading}
                    width="100%"
                  />
                ) : (
                  <div className={styles.permissionMessage}>
                    You don’t have permission for partner mapping.
                  </div>
                )}
              </Col>
            </Row>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerAndPlan;
